import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import Loader from "@/components/Loader";
import { useEntitlements } from "@/hooks/useEntitlements";
import { useCapabilityLanding } from "@/hooks/useCapabilityLanding";
import { useSnackbar } from "@/contexts/SnackbarContext";
import type { Capability } from "@/types/entitlements";
import type { GuardProps } from "./types";

interface CapabilityGuardProps extends GuardProps {
  requires: Capability;
}

const MESSAGE: Record<Capability, string> = {
  sales: "None of your properties are on a sales contract.",
  revenue: "None of your properties are on a revenue contract.",
};

function CapabilityGuard({ children, requires }: CapabilityGuardProps) {
  const { loading, capabilities } = useEntitlements();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { destination } = useCapabilityLanding();
  const allowed = capabilities.includes(requires);

  useEffect(() => {
    if (loading || allowed) return;
    showSnackbar(MESSAGE[requires], "warning");
    navigate(destination, { replace: true });
  }, [loading, allowed, requires, destination, navigate, showSnackbar]);

  if (loading) return <Loader />;
  if (!allowed) return null;

  return <>{children}</>;
}

export default CapabilityGuard;
